// The shared live 3D viewport: the actual proof that Studio and Sunder
// render identically from the same document, since both call the exact same
// @sstk/weapon-kit buildWeaponModel with the same document shape. Reuses
// Map Builder/Character Studio's viewport pattern (Viewport wraps
// react-three-fiber's Canvas, or projects into a shared ViewportHost canvas
// when one is mounted above it - see src/engine/runtime/viewport/Viewport.tsx).
//
// Always rebuilds via the real buildWeaponModel on every document change -
// no bespoke partial-update path that reimplements pieces of the shared
// pipeline, since that would risk exactly the preview/in-game drift this
// whole package exists to prevent. Structural edits (chassis, mounted
// components) and skin edits are all just "the document changed" here.
// Rapid edits (a colour slider drag) are debounced client-side so a full
// async rebuild doesn't fire on every pointer-move event.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import { buildWeaponModel, updateSkinAnimation, type WeaponRegistry } from '@sstk/weapon-kit';
import type { WeaponDocumentInput } from '@sstk/weapon-schema';

import { Viewport } from '../../../engine/runtime/viewport/Viewport';
import type { WeaponDocument } from '../../../engine/content/schema/weapon';
import { CHASSIS_REGISTRY, COMPONENT_REGISTRY } from '@sstk/weapon-catalogue';
import { gunsmithAssetResolver } from '../catalogue/assets';

const REGISTRY: WeaponRegistry = { chassis: CHASSIS_REGISTRY, components: COMPONENT_REGISTRY };

/** Rebuild coalescing window: collapses a slider drag's event flood into one rebuild. */
const REBUILD_DEBOUNCE_MS = 80;

function disposeMaterials(root: THREE.Object3D): void {
  // Geometry is loader-cached and shared across every clone
  // (@sstk/weapon-kit's instantiateGlb docs), so only materials - always
  // freshly created per weapon instance - are safe and correct to dispose.
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) material?.dispose();
  });
}

interface WeaponModelProps {
  doc: WeaponDocument;
  spin: boolean;
  onError: (message: string | null) => void;
}

function WeaponModel({ doc, spin, onError }: WeaponModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const [model, setModel] = useState<THREE.Group | null>(null);

  useEffect(() => {
    let cancelled = false;
    const docForBuild: WeaponDocumentInput = doc;
    const timer = setTimeout(() => {
      buildWeaponModel(docForBuild, REGISTRY, gunsmithAssetResolver)
        .then((group) => {
          if (cancelled) {
            disposeMaterials(group);
            return;
          }
          onError(null);
          setModel((previous) => {
            if (previous) disposeMaterials(previous);
            return group;
          });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          onError(error instanceof Error ? error.message : 'Failed to build weapon model.');
        });
    }, REBUILD_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // doc is a plain object rebuilt on every store edit, so identity change
    // alone is the correct trigger - a deep-equal check would just re-derive
    // what the store's own undo/redo snapshots already guarantee changed.
  }, [doc, onError]);

  useEffect(() => {
    return () => {
      if (model) disposeMaterials(model);
    };
    // Only on unmount - the effect above already disposes the *previous*
    // model on every successful rebuild, so this only needs to catch the
    // last one when WeaponModel itself goes away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((state, delta) => {
    if (groupRef.current && spin) groupRef.current.rotation.y += delta * 0.4;
    if (model) updateSkinAnimation(model, state.clock.elapsedTime);
  });

  if (!model) return null;
  return (
    <group ref={groupRef}>
      <primitive object={model} />
    </group>
  );
}

function StageLights() {
  return (
    <>
      <ambientLight intensity={1.0} />
      <directionalLight position={[3, 5, 4]} intensity={2.2} castShadow />
      <directionalLight position={[-4, 2, -3]} intensity={0.5} color="#cfd8e6" />
    </>
  );
}

export interface WeaponViewportProps {
  doc: WeaponDocument;
}

/** The shared live 3D preview: one document in, one real Three.js render out. */
export function WeaponViewport({ doc }: WeaponViewportProps) {
  const [spin, setSpin] = useState(true);
  const [ground, setGround] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onError = useMemo(() => (message: string | null) => setError(message), []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        style={{
          position: 'absolute', left: 12, top: 12, zIndex: 2,
          display: 'flex', gap: 6,
        }}
      >
        <button
          type="button"
          onClick={() => setSpin((v) => !v)}
          style={{
            border: '1px solid ' + (spin ? 'var(--ss-accent-border, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
            background: spin ? 'var(--ss-accent-soft, rgba(100,68,214,0.16))' : 'transparent',
            color: spin ? 'var(--ss-accent, #6444d6)' : 'var(--ss-text-3, #6d747e)',
            borderRadius: 7, padding: '4px 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em',
          }}
        >
          {spin ? 'SPIN ✓' : 'SPIN'}
        </button>
        <button
          type="button"
          onClick={() => setGround((v) => !v)}
          style={{
            border: '1px solid ' + (ground ? 'var(--ss-accent-border, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
            background: ground ? 'var(--ss-accent-soft, rgba(100,68,214,0.16))' : 'transparent',
            color: ground ? 'var(--ss-accent, #6444d6)' : 'var(--ss-text-3, #6d747e)',
            borderRadius: 7, padding: '4px 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em',
          }}
        >
          GROUND
        </button>
      </div>
      {error && (
        <div
          role="alert"
          style={{
            position: 'absolute', left: 12, right: 12, bottom: 12, zIndex: 2,
            background: 'rgba(120,20,20,0.85)', color: '#ffe3e3', borderRadius: 8,
            padding: '8px 12px', fontSize: 11.5, lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}
      <Viewport shadows>
        <PerspectiveCamera makeDefault position={[0.5, 0.28, 0.55]} fov={38} />
        <color attach="background" args={['#0e1116']} />
        <StageLights />
        {ground && (
          <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <circleGeometry args={[1.2, 48]} />
            <meshStandardMaterial color="#181b1f" roughness={0.92} />
          </mesh>
        )}
        <WeaponModel doc={doc} spin={spin} onError={onError} />
        <OrbitControls enablePan={false} minDistance={0.15} maxDistance={2.2} target={[0, 0.06, 0]} />
      </Viewport>
    </div>
  );
}
